/**
 * RecuperarSenhaPage (`/recuperar-senha`, RF-M11-001) — recuperação de senha.
 *
 * Fluxo Cognito nativo (accountRecovery EMAIL_ONLY, envio por SES): (1) solicita
 * o código informando o e-mail; (2) confirma com o código + nova senha.
 * Respostas neutras/genéricas (RN-GLOBAL-004): a solicitação nunca revela se a
 * conta existe.
 */

import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CognitoClient } from '../services/cognitoClient';
import { CognitoIdentityClient } from '../services/cognitoIdentityClient';
import { loadRuntimeConfig } from '../config/runtime-config';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';

export interface RecuperarSenhaPageProps {
  /** Cliente Cognito (dublável em testes). Default: derivado do runtime config. */
  readonly cognitoClient?: CognitoClient;
}

type Etapa = 'solicitar' | 'confirmar' | 'concluido';

export function RecuperarSenhaPage({ cognitoClient }: RecuperarSenhaPageProps): JSX.Element {
  const whiteLabel = useWhiteLabel();
  const client = useMemo<CognitoClient>(
    () => cognitoClient ?? new CognitoIdentityClient(loadRuntimeConfig().cognito),
    [cognitoClient],
  );

  const [etapa, setEtapa] = useState<Etapa>('solicitar');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function solicitar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await client.forgotPassword(email);
      setEtapa('confirmar');
    } catch {
      setErro('Não foi possível concluir a operação. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  async function confirmar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await client.confirmForgotPassword(email, code.trim(), novaSenha);
      setEtapa('concluido');
    } catch {
      setErro('Não foi possível redefinir a senha. Verifique o código e tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-page" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <div className="auth-card">
        <h1 className="auth-title">Recuperar senha</h1>

        {etapa === 'solicitar' && (
          <form onSubmit={solicitar} aria-labelledby="rec-title">
            <p className="auth-subtitle" id="rec-title">
              Informe seu e-mail para receber o código de recuperação.
            </p>
            <label className="field">
              <span>E-mail</span>
              <input
                autoFocus
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
            </label>
            {erro && (
              <p className="form-error" role="alert">
                {erro}
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar código'}
            </button>
          </form>
        )}

        {etapa === 'confirmar' && (
          <form onSubmit={confirmar} aria-labelledby="conf-title">
            <p className="auth-subtitle" id="conf-title">
              Enviamos um código para o e-mail informado. Digite-o e escolha uma nova senha.
            </p>
            <label className="field">
              <span>Código</span>
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                required
                value={code}
                onChange={(ev) => setCode(ev.target.value)}
              />
            </label>
            <label className="field">
              <span>Nova senha</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={novaSenha}
                onChange={(ev) => setNovaSenha(ev.target.value)}
              />
            </label>
            {erro && (
              <p className="form-error" role="alert">
                {erro}
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={enviando}>
              {enviando ? 'Redefinindo…' : 'Redefinir senha'}
            </button>
          </form>
        )}

        {etapa === 'concluido' && (
          <p className="notice" role="status">
            Senha redefinida. Você já pode entrar com a nova senha.
          </p>
        )}

        <Link to="/login" className="auth-link">
          Voltar ao login
        </Link>
      </div>
    </div>
  );
}
